import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiErr, photoUrl, originalPhotoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";
import { fmtDateTime } from "@/lib/format";
import { EVIDENCE } from "@/constants/testIds";
import { Layers, FileCheck, Camera, Activity, MapPin, ArrowDown, PencilLine, Check, GitCompare, Undo2 } from "lucide-react";
import { toast } from "sonner";

export function TraceModal({ packId, item, open, onClose }) {
  const { hasRole } = useAuth();
  const canEdit = hasRole("tenant_admin", "contract_manager", "supervisor");
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [captions, setCaptions] = useState({});
  const [markup, setMarkup] = useState(null); // photo being annotated
  const [compareId, setCompareId] = useState(null); // photo showing original vs marked-up

  const loadChain = useCallback(() => {
    if (!open || !item) return;
    setLoading(true);
    api
      .get(`/evidence-packs/${packId}/trace/${item.id}`)
      .then((r) => {
        setChain(r.data);
        const c = {};
        (r.data.evidence || []).forEach((p) => { c[p.id] = p.caption || ""; });
        setCaptions(c);
      })
      .finally(() => setLoading(false));
  }, [open, item, packId]);

  useEffect(() => {
    if (open && item) loadChain();
    else { setChain(null); setMarkup(null); setCompareId(null); }
  }, [open, item, loadChain]);

  const saveCaption = async (id) => {
    try {
      await api.patch(`/photos/${id}`, { caption: captions[id] });
      toast.success("Caption saved");
      loadChain();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveMarkup = async (dataUrl) => {
    try {
      await api.patch(`/photos/${markup.id}`, { data_url: dataUrl });
      toast.success("Markup saved to evidence");
      setMarkup(null);
      loadChain();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const revert = async (id) => {
    try {
      await api.post(`/photos/${id}/revert`);
      toast.success("Reverted to the original photo");
      setCompareId(null);
      loadChain();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={EVIDENCE.traceModal}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Layers className="h-5 w-5 text-forest-mid" /> Evidence trail
          </DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-muted-foreground">Tracing…</p>}
        {chain && (
          <div className="space-y-3">
            <div className="rounded-lg border border-forest-mid/30 bg-sage-surface p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-forest-mid">Pack metric</p>
              <p className="mt-1 text-xl font-bold font-heading text-forest-dark">
                {chain.metric.label}: {chain.metric.value ?? "—"} {chain.metric.unit}
              </p>
              {chain.metric.target != null && (
                <p className="text-sm text-muted-foreground">Target: {chain.metric.target}</p>
              )}
            </div>

            <div className="flex justify-center"><ArrowDown className="h-5 w-5 text-muted-foreground" /></div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Source activities</p>
              <div className="space-y-2">
                {chain.activities.length === 0 && <p className="text-sm text-muted-foreground">No linked activity records.</p>}
                {chain.activities.map((a, i) => (
                  <div key={i} className="rounded-lg border bg-white p-3">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      {a.type === "indicator" ? <Activity className="h-4 w-4 text-forest-mid" /> : <FileCheck className="h-4 w-4 text-forest-mid" />}
                      {a.title || a.name}
                      {a.status && <span className="text-xs font-normal text-muted-foreground">({a.status})</span>}
                    </div>
                    {a.capture && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Submitted by {a.capture.submitted_by_name} · {fmtDateTime(a.capture.submitted_at)}
                      </div>
                    )}
                    {a.readings && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {a.readings.map((r) => `${r.period}: ${r.value}`).join("  ·  ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {chain.evidence.length > 0 && (
              <>
                <div className="flex justify-center"><ArrowDown className="h-5 w-5 text-muted-foreground" /></div>
                <div>
                  <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Camera className="h-3.5 w-3.5" /> Original evidence
                    {canEdit && <span className="font-normal normal-case text-forest-mid"> · tap a photo to mark up or caption</span>}
                  </p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {chain.evidence.map((p) => (
                      <div key={p.id} className="overflow-hidden rounded-lg border bg-white">
                        <div className="relative">
                          <img src={photoUrl(p)} alt={p.caption || "evidence"} className="h-28 w-full object-cover" />
                          {p.annotated && <span className="absolute right-0 top-0 rounded-bl bg-terracotta px-1 text-[9px] text-white"><PencilLine className="inline h-2.5 w-2.5" /></span>}
                        </div>
                        {compareId === p.id && p.has_original && (
                          <div className="grid grid-cols-2 gap-1 bg-slate-900 p-1">
                            <div className="relative"><img src={originalPhotoUrl(p)} alt="original" className="h-24 w-full rounded object-cover" /><span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[8px] font-semibold text-white">ORIGINAL</span></div>
                            <div className="relative"><img src={photoUrl(p)} alt="marked up" className="h-24 w-full rounded object-cover" /><span className="absolute left-1 top-1 rounded bg-terracotta px-1 text-[8px] font-semibold text-white">MARKED-UP</span></div>
                          </div>
                        )}
                        <div className="space-y-1.5 p-2 text-[11px] text-muted-foreground">
                          {p.lat != null && (
                            <div className="flex items-center gap-1 font-mono">
                              <MapPin className="h-3 w-3" /> {p.lat?.toFixed(3)}, {p.lng?.toFixed(3)}
                            </div>
                          )}
                          {canEdit ? (
                            <>
                              <div className="flex gap-1">
                                <Input value={captions[p.id] ?? ""} onChange={(e) => setCaptions((c) => ({ ...c, [p.id]: e.target.value }))}
                                  placeholder="Caption" className="h-7 text-xs" data-testid={EVIDENCE.captionInput(p.id)} />
                                <Button size="icon" variant="outline" className="h-7 w-7 shrink-0" onClick={() => saveCaption(p.id)} data-testid={EVIDENCE.captionSaveBtn(p.id)}><Check className="h-3.5 w-3.5" /></Button>
                              </div>
                              <Button size="sm" variant="outline" className="h-7 w-full text-xs" onClick={() => setMarkup(p)} data-testid={EVIDENCE.annotatePhotoBtn(p.id)}>
                                <PencilLine className="mr-1 h-3 w-3" /> Mark up
                              </Button>
                              {p.has_original && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" className="h-7 flex-1 text-xs" onClick={() => setCompareId(compareId === p.id ? null : p.id)} data-testid={EVIDENCE.compareBtn(p.id)}>
                                    <GitCompare className="mr-1 h-3 w-3" /> {compareId === p.id ? "Hide" : "Compare"}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 flex-1 text-xs text-terracotta hover:text-terracotta" onClick={() => revert(p.id)} data-testid={EVIDENCE.revertBtn(p.id)}>
                                    <Undo2 className="mr-1 h-3 w-3" /> Revert
                                  </Button>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="truncate">{p.caption || "Photo"}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <Dialog open={!!markup} onOpenChange={(v) => !v && setMarkup(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Mark up evidence photo</DialogTitle></DialogHeader>
            {markup && (
              <PhotoAnnotator src={photoUrl(markup)} onSkip={() => setMarkup(null)} onSave={saveMarkup} />
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
